import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ClientCard } from '@/api/base-types';
import { useBaseMutation, useClient, useModules, useUsers } from '@/api/base';
import { api } from '@/api/client';
import { ActionMenu, RowActions, type MenuItem } from '@/components/kit/ActionMenu';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { Breadcrumbs, Kv } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { Alert, ErrorState, Note, Skeleton } from '@/components/kit/Note';
import { ObjectTabs } from '@/components/kit/ObjectTabs';
import { OwnerPill } from '@/components/kit/OwnerPill';
import { Overview, Panel, PanelEmpty, Signals } from '@/components/kit/Panel';
import { LinkRow, PersonHeader } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { useToast } from '@/components/kit/Toast';
import { formatPhone, years } from '@/lib/phone';
import { titleInitials } from '@/lib/text';
import { ArchiveModal } from '../shared/ArchiveModal';
import { FeedPanel } from '../shared/FeedPanel';
import { JournalModal } from '../shared/JournalModal';
import { CONTACT_ROLE, REP_KIND, roleLabels, ruDate } from '../shared/labels';
import { MergeModal } from '../shared/MergeModal';
import { ClientDeals, ClientHistory } from '../shared/Participation';

type ClientTab = 'ov' | 'deals' | 'hist';
import { useLinkActions } from '../shared/useLinkActions';
import { errorText } from '../shared/useUndoable';
import {
  AcceptanceModal,
  AddRepresentativeModal,
  AddWardModal,
  EditClientModal,
  ToStudioModal,
  ToTeamModal,
} from './ClientDialogs';
import { ClientDataRows } from './ClientData';

type Dialog = 'edit' | 'team' | 'rep' | 'ward' | 'studio' | 'acceptance' | 'merge' | 'journal' | 'archive' | 'delete';

/**
 * Карточка клиента (БЛ 11.2, viewCard в макете). Вкладки «Сделки»,
 * «Финансы», «Рефералка», «История участия» и блок «Задачи» появятся
 * вместе со своими модулями (ТЗ M2 7.3); пока их нет, карточка — «Обзор»
 * без ряда вкладок, а главная кнопка — «Изменить».
 */
export function ClientPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const card = useClient(clientId);
  const modules = useModules();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [tab, setTab] = useState<ClientTab>('ov');
  const close = () => setDialog(null);

  if (card.isError) {
    return (
      <div className="main">
        <Breadcrumbs items={[{ label: 'Клиенты', onClick: () => navigate('/clients') }, { label: 'Клиент' }]} />
        <ErrorState message={card.error.message} onRetry={() => void card.refetch()} />
      </div>
    );
  }
  if (card.isLoading || !card.data) {
    return (
      <div className="main">
        <Breadcrumbs items={[{ label: 'Клиенты', onClick: () => navigate('/clients') }, { label: '…' }]} />
        <Skeleton width={320} height={28} />
        <div className="sigs" />
        <Skeleton width="60%" />
      </div>
    );
  }

  const c = card.data;
  // «Сделки» и «История участия» — за флагами модуля M3 (ТЗ M3 7.2)
  const tabs: { value: ClientTab; label: string }[] = [
    { value: 'ov', label: 'Обзор' },
    ...(modules.data?.deals ? [{ value: 'deals' as const, label: 'Сделки' }] : []),
    ...(modules.data?.registrations ? [{ value: 'hist' as const, label: 'История участия' }] : []),
  ];
  const current: ClientTab = tabs.some((t) => t.value === tab) ? tab : 'ov';

  return (
    <div className="main has-abar">
      <Breadcrumbs items={[{ label: 'Клиенты', onClick: () => navigate('/clients') }, { label: c.fullName }]} />
      <ClientHeader client={c} onDialog={setDialog} tasks={modules.data?.tasks ?? false} />

      <Signals>
        {c.signals.possibleDuplicateOf ? (
          <Alert
            action={
              <>
                <Button small onClick={() => navigate(`/clients/${c.signals.possibleDuplicateOf!.id}`)}>
                  Открыть её
                </Button>
                <Button small onClick={() => setDialog('merge')}>
                  Сравнить и слить…
                </Button>
              </>
            }
          >
            <b>Возможный дубль.</b>
            <span>Совпадают фамилия, имя и дата рождения с карточкой «{c.signals.possibleDuplicateOf.fullName}».</span>
          </Alert>
        ) : null}
      </Signals>

      <ObjectTabs items={tabs} value={current} onChange={setTab} />

      {current === 'deals' ? <ClientDeals clientId={c.id} /> : null}
      {current === 'hist' ? <ClientHistory clientId={c.id} /> : null}
      {current === 'ov' ? (
        <Overview
          left={
            <>
              <DataPanel client={c} />
              <TeamsPanel client={c} onAdd={() => setDialog('team')} />
              {c.roles.includes('participant') ? (
                <DocumentsPanel client={c} onMark={() => setDialog('acceptance')} />
            ) : null}
            {(c.roles.includes('participant') && c.isMinor) || c.representatives.length ? (
              <RepresentativesPanel client={c} onAdd={() => setDialog('rep')} />
            ) : null}
            {c.wards.length || !c.isMinor ? <WardsPanel client={c} onAdd={() => setDialog('ward')} /> : null}
            {c.studios.length || !c.isMinor ? <StudiosPanel client={c} onAdd={() => setDialog('studio')} /> : null}
          </>
        }
        right={<FeedPanel entity="client" id={c.id} placeholder="Что важно знать о клиенте" />}
      />
      ) : null}

      {dialog === 'edit' ? <EditClientModal client={c} onClose={close} /> : null}
      {dialog === 'team' ? <ToTeamModal client={c} onClose={close} /> : null}
      {dialog === 'rep' ? <AddRepresentativeModal client={c} onClose={close} /> : null}
      {dialog === 'ward' ? <AddWardModal client={c} onClose={close} /> : null}
      {dialog === 'studio' ? <ToStudioModal client={c} onClose={close} /> : null}
      {dialog === 'acceptance' ? <AcceptanceModal client={c} onClose={close} /> : null}
      {dialog === 'journal' ? <JournalModal entity="client" id={c.id} onClose={close} /> : null}
      {dialog === 'archive' ? <ArchiveModal entity="client" id={c.id} name={c.fullName} onClose={close} /> : null}
      {dialog === 'merge' ? (
        <MergeModal entity="client" id={c.id} preselect={c.signals.possibleDuplicateOf?.id} onClose={close} />
      ) : null}
      {dialog === 'delete' ? <DeleteClientModal client={c} onClose={close} /> : null}
    </div>
  );
}

function ClientHeader({
  client: c,
  onDialog,
  tasks,
}: {
  client: ClientCard;
  onDialog: (d: Dialog) => void;
  tasks: boolean;
}) {
  const users = useUsers();
  const toast = useToast();
  const setOwner = useBaseMutation((responsibleUserId: string | null) =>
    api.patch(`/clients/${c.id}`, { responsibleUserId }),
  );
  const unarchive = useBaseMutation(() => api.post(`/clients/${c.id}/unarchive`));

  const items: MenuItem[] = [
    ...(c.roles.includes('participant')
      ? [{ label: 'Отметить принятие документов…', onClick: () => onDialog('acceptance') }]
      : []),
    { label: 'Слить с дублем…', onClick: () => onDialog('merge') },
    { label: 'Журнал изменений', onClick: () => onDialog('journal') },
    c.archivedAt
      ? {
          label: 'Вернуть из архива',
          onClick: () =>
            unarchive.mutate(undefined, {
              onSuccess: () => toast.show('Клиент снова в работе'),
              onError: (e) => toast.showError(errorText(e)),
            }),
        }
      : { label: 'В архив…', danger: true, onClick: () => onDialog('archive') },
    ...(c.canDelete
      ? [
          {
            label: 'Удалить',
            description: 'карточка без связей и истории — заведена по ошибке',
            danger: true,
            onClick: () => onDialog('delete'),
          },
        ]
      : []),
  ];

  return (
    <PersonHeader
      initials={c.initials}
      title={c.fullName}
      pills={
        <>
          {roleLabels(c.roles, c.studioRoles).map((label) => (
            <Pill key={label} tone="mut">
              {label}
            </Pill>
          ))}
          {c.archivedAt ? <Pill tone="mut">В архиве</Pill> : null}
        </>
      }
      meta={
        <>
          {c.phone ? (
            <a className="lnk num" href={`tel:${c.phone}`}>
              {formatPhone(c.phone)}
            </a>
          ) : (
            <span className="off">телефона нет</span>
          )}
          {c.email ? (
            <a className="lnk" href={`mailto:${c.email}`}>
              {c.email}
            </a>
          ) : null}
          <OwnerPill
            owner={c.responsible}
            users={users.data ?? []}
            onChange={(id) =>
              setOwner.mutate(id, {
                onSuccess: () => toast.show(id ? 'Ответственный назначен' : 'Ответственный снят'),
                onError: (e) => toast.showError(errorText(e)),
              })
            }
          />
        </>
      }
      actions={
        <>
          {/* Пока модуля «Задачи» нет, главная кнопка — «Изменить» (ТЗ M2 7.3) */}
          {!tasks ? (
            <Button tone="primary" onClick={() => onDialog('edit')}>
              Изменить
            </Button>
          ) : null}
          <ActionMenu items={items} />
        </>
      }
    />
  );
}

function DataPanel({ client: c }: { client: ClientCard }) {
  return (
    <Panel title="Данные">
      <ClientDataRows client={c} />
    </Panel>
  );
}

function TeamsPanel({ client: c, onAdd }: { client: ClientCard; onAdd: () => void }) {
  const navigate = useNavigate();
  const links = useLinkActions();
  return (
    <Panel
      title="Команды"
      count={c.teams.length}
      actions={
        <Button small onClick={onAdd}>
          Включить в команду
        </Button>
      }
    >
      {c.teams.length ? (
        c.teams.map(({ team: t, isHead, membership: m }) => {
          // Подпись по доработке M2-1 п. 4: руководителю — «руководит»,
          // участнику — чья команда; дата включения больше не показывается
          const role = isHead
            ? m && !m.leftOn
              ? 'руководит и выступает'
              : 'руководит'
            : m?.leftOn
              ? `рук. ${t.head.shortName} · выбыл(а) ${ruDate(m.leftOn)}`
              : `рук. ${t.head.shortName}`;
          const sub: ReactNode[] = [
            !t.dataConfirmed && isHead ? <span className="wtx">данные ждут руководителя</span> : null,
            role,
            t.studio ? `студия ${t.studio.name}` : 'без студии',
            t.city,
          ].filter(Boolean);
          return (
            <LinkRow
              key={t.id}
              initials={titleInitials(t.name)}
              name={t.name}
              sub={sub.map((s, i) => (
                <span key={i}>
                  {i ? ' · ' : ''}
                  {s}
                </span>
              ))}
              value={
                m ? (
                  <RowActions
                    items={links.membership({ id: m.id, teamId: t.id, clientId: c.id, leftOn: m.leftOn, name: c.fullName })}
                  />
                ) : null
              }
              onClick={() => navigate(`/teams/${t.id}`)}
            />
          );
        })
      ) : (
        <PanelEmpty>Не состоит ни в одной команде и не руководит ими.</PanelEmpty>
      )}
    </Panel>
  );
}

const RELATION: Record<string, string> = {
  ...REP_KIND,
  head: 'руководитель команды',
  other: 'представитель',
};

function DocumentsPanel({ client: c, onMark }: { client: ClientCard; onMark: () => void }) {
  const navigate = useNavigate();
  const a = c.acceptance;
  return (
    <Panel
      title="Документы и согласие"
      actions={
        !a || c.needsOwnAcceptance ? (
          <Button small onClick={onMark}>
            Отметить вручную
          </Button>
        ) : null
      }
    >
      {a ? (
        <>
          {c.needsOwnAcceptance ? (
            <div className="mnote">
              <Note>
                Исполнилось 18 лет. Документы принимал(а) представитель; если участник оформит что-то на сайте сам,
                отметка станет его собственной.
              </Note>
            </div>
          ) : null}
          <Kv k="Состояние">{c.needsOwnAcceptance ? 'действует до замены' : 'приняты'}</Kv>
          <Kv k="Документы">оферта, политика и согласие на обработку и публикацию</Kv>
          <Kv k="Принял(а)">
            {a.acceptedBy.relation === 'self' ? (
              'сам участник'
            ) : (
              <>
                <TextLink onClick={() => navigate(`/clients/${a.acceptedBy.id}`)}>{a.acceptedBy.fullName}</TextLink>{' '}
                <span className="muted">· {RELATION[a.acceptedBy.relation]}</span>
              </>
            )}
          </Kv>
          <Kv k="Когда">
            <span className="num">{ruDate(a.acceptedAt)}</span>
          </Kv>
          <Kv k="Как">
            {a.method === 'site' ? (
              <>
                галочкой на сайте{a.sourceRef ? <span className="muted"> · {a.sourceRef}</span> : null}
              </>
            ) : (
              'отмечено менеджером'
            )}
          </Kv>
        </>
      ) : (
        <Note>
          Отдельной отметки нет. За участников номера документы принимает руководитель, когда регистрирует команду на
          сайте. Собственная отметка появится, если {c.isMinor ? 'представитель' : 'участник'} сам оформит что-то на
          сайте.
        </Note>
      )}
    </Panel>
  );
}

function RepresentativesPanel({ client: c, onAdd }: { client: ClientCard; onAdd: () => void }) {
  const navigate = useNavigate();
  const links = useLinkActions();
  return (
    <Panel
      title="Представители"
      count={c.representatives.length}
      actions={
        <Button small onClick={onAdd}>
          Добавить
        </Button>
      }
    >
      {c.representatives.length ? (
        c.representatives.map((r) => (
          <LinkRow
            key={r.id}
            initials={titleInitials(r.client.fullName)}
            name={r.client.fullName}
            sub={`${REP_KIND[r.kind]} · ${formatPhone(r.client.phone)}`}
            value={<RowActions items={links.representative(r.id)} />}
            onClick={() => navigate(`/clients/${r.client.id}`)}
          />
        ))
      ) : (
        <Note>
          Представитель не указан.{c.age !== null ? ` Участнику ${years(c.age)} — ` : ' '}родителя можно добавить, если он
          с нами на связи; для участия в номере это не обязательно.
        </Note>
      )}
    </Panel>
  );
}

function WardsPanel({ client: c, onAdd }: { client: ClientCard; onAdd: () => void }) {
  const navigate = useNavigate();
  const links = useLinkActions();
  return (
    <Panel
      title="Подопечные"
      count={c.wards.length}
      actions={
        <Button small onClick={onAdd}>
          Добавить ребёнка
        </Button>
      }
    >
      {c.wards.length ? (
        c.wards.map((w) => (
          <LinkRow
            key={w.id}
            initials={titleInitials(w.client.fullName)}
            name={w.client.fullName}
            sub={[w.kind === 'other' ? 'подопечный' : 'ребёнок', w.client.age !== null ? years(w.client.age) : '', ...w.client.teams]
              .filter(Boolean)
              .join(' · ')}
            value={<RowActions items={links.representative(w.id)} />}
            onClick={() => navigate(`/clients/${w.client.id}`)}
          />
        ))
      ) : (
        <PanelEmpty>Детей и подопечных нет.</PanelEmpty>
      )}
    </Panel>
  );
}

function StudiosPanel({ client: c, onAdd }: { client: ClientCard; onAdd: () => void }) {
  const navigate = useNavigate();
  const links = useLinkActions();
  return (
    <Panel
      title="Студии"
      count={c.studios.length}
      actions={
        <Button small onClick={onAdd}>
          Привязать к студии
        </Button>
      }
    >
      {c.studios.length ? (
        c.studios.map((s) => (
          <LinkRow
            key={s.id}
            initials={titleInitials(s.studio.name)}
            name={s.studio.name}
            sub={[CONTACT_ROLE[s.role], s.isMain ? 'основной контакт' : '', s.studio.city].filter(Boolean).join(' · ')}
            value={<RowActions items={links.contact(s)} />}
            onClick={() => navigate(`/studios/${s.studio.id}`)}
          />
        ))
      ) : (
        <PanelEmpty>Не связан ни с одной студией.</PanelEmpty>
      )}
    </Panel>
  );
}

/** Удаление необратимо — через подтверждение (кит: опасное действие из «⋯»). */
function DeleteClientModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  // Переход — в колбэке самой мутации, а не mutate(): после удаления карточка
  // перезапрашивается с 404, страница уходит в ошибку и размонтирует окно,
  // а колбэки mutate() у размонтированного компонента не вызываются
  const remove = useBaseMutation(() => api.delete(`/clients/${client.id}`), {
    onSuccess: () => {
      toast.show('Клиент удалён');
      navigate('/clients');
    },
  });
  return (
    <Modal
      title="Удалить клиента?"
      onClose={onClose}
      confirm={{
        label: 'Удалить',
        danger: true,
        disabled: remove.isPending,
        onClick: () => remove.mutate(undefined, { onError: (e) => toast.showError(errorText(e)) }),
      }}
    >
      <b>{client.fullName}</b> — карточка без связей и истории. Она исчезнет совсем; в журнале останется след.
    </Modal>
  );
}
